/**
 * @file
 * Date formatting and arithmetic.
 *
 * @example
 * $notify($date.data.timeZone);
 * @example
 * $notify($date.long($date.local()));
 * @example
 * $notify($date.mmmdy($date.local()));
 * @example
 * $notify($date.hmsz($date.local()));
 */


import $locale from './locale';


const re_date = /^([+-]?\d{4}(?!\d\d\b))(?:-?(?:(0[1-9]|1[0-2])(?:-?([12]\d|0[1-9]|3[01]))?)(?:[T\s](?:(?:([01]\d|2[0-3])(?::?([0-5]\d))?|24\:?00)([.,]\d+(?!:))?)?(?::?([0-5]\d)(?:[.,](\d+))?)?([zZ]|([+-](?:[01]\d|2[0-3])):?([0-5]\d)?)?)?)?$/;
const re_diff = /^([+-]?)P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/;


const pad = n => n < 10 ? '0' + n : n;

const unitSecond  = 1000;
const unitMinute  = 1000 * 60;
const unitHour    = 1000 * 60 * 60;
const unitDay     = 1000 * 60 * 60 * 24;
const unitYear    = 1000 * 60 * 60 * 24 * 365;

const emDash = '—';

// Regions (or language, if region not specified) that begin their week on Saturday or Sunday
const regionSat = 'AEAFBHDJDZEGIQIRJOKWLYOMQASDSY'.match(/../g);
const regionSun = 'AGARASAUBDBRBSBTBWBZCACNCODMDOETGTGUHKHNIDILINJMJPKEKHKRLAMHMMMOMTMXMZNINPPAPEPHPKPRPTPYSASGSVTHTTTWUMUSVEVIWSYEZAZW'.match(/../g);
const languageSat = ['ar','arq','arz','fa'];
const languageSun = 'amasbndzengnguhehiidjajvkmknkolomhmlmrmtmyneomorpapssdsmsnsutatethtnurzhzu'.match(/../g);

// Date object accessor function names (minus the get or set)
const accessors = {
    millisecond: 'Milliseconds',
    second: 'Seconds',
    minute: 'Minutes',
    hour: 'Hours',
    day: 'Date',
    month: 'Month',
    year: 'FullYear'
};

// Precompiled formatters
const numeric = 'numeric';
const digit = '2-digit';
const long = 'long';
const short = 'short';
const formatters = {
    hm: [,,,,numeric,numeric],
    hms: [,,,,numeric,numeric,numeric],
    hmsz: [,,,,numeric,numeric,numeric,short],
    hhmm: [,,,,digit,digit],
    hhmmss: [,,,,digit,digit,digit],
    hhmmssz: [,,,,digit,digit,digit,short],
    m: [,,numeric],
    d: [,numeric],
    y: [,,,numeric],
    mdy: [],
    mdyhm: [,numeric,numeric,numeric,numeric,numeric],
    mdyhms: [,numeric,numeric,numeric,numeric,numeric,numeric],
    mdyhmsz: [,numeric,numeric,numeric,numeric,numeric,numeric,short],
    mm: [,,digit],
    dd: [,digit],
    yy: [,,,digit],
    mmdd: [,digit,digit],
    mmddyy: [,digit,digit,numeric],
    mmm: [,,short],
    mmmd: [,numeric,short],
    mmmy: [,,short,numeric],
    mmmdy: [,numeric,short,numeric],
    mmmm: [,,long],
    mmmmd: [,numeric,long],
    mmmmy: [,,long,numeric],
    mmmmdhm: [,numeric,long,,numeric,numeric],
    mmmmdy: [,numeric,long,numeric],
    wmmmmdy: [long,numeric,long,numeric],
    short: [,numeric,short,numeric,numeric,numeric],
    long: [long,numeric,long,numeric,numeric,numeric]
};

// Formatter used in calculating time zone offset for a specific time in the current time zone
let testOffset;
let baseOffset;
let getOffset = (formatter, utc) => {
    const date = new Date(formatter.format(utc));
    return date - date.getTimezoneOffset() * 6e4 - (utc - utc % 1000);
}

let dirty = 0;


let _now;
const now = () => {
    if (!_now) {
        _now = Date.now();
        setTimeout(() => { _now = 0; }, 1000);
    }
    return _now;
};


const prototype = {
    add(diff) {
        const g = diff.match(re_diff);
        if (!g) {
            throw new RangeError('Invalid duration');
        }
        const sign = g[1] + 1 | 0;
        if (g[2]) {
            this.year += sign * g[2];
        }
        if (g[3]) {
            this.month += sign * g[3];
        }
        if (g[4]) {
            this.day += sign * g[4] * 7;
        }
        if (g[5]) {
            this.day += sign * g[5];
        }
        if (g[6]) {
            this.hour += sign * g[6];
        }
        if (g[7]) {
            this.minute += sign * g[7];
        }
        if (g[8]) {
            this.second += sign * g[8];
        }
        return this;
    },

    valueOf() {
        return +this.value;
    },

    unix() {
        return this.value / 1000 | 0;
    },

    isValid() {
        return !isNaN(this.value);
    },

    isEqual(other) {
        return +this.value == +other;
    },

    diffSeconds(other) {
        return (this.value - other) / unitSecond | 0;
    },

    diffMinutes(other) {
        return (this.value - other) / unitMinute | 0;
    },

    diffHours(other) {
        return (this.value - other) / unitHour | 0;
    },

    diffDays(other) {
        return (this.value - other) / unitDay | 0;
    },

    previousYear() {
        this.year--;
        return this;
    },

    previousMonth() {
        this.month--;
        return this;
    },

    previousDay() {
        this.day--;
        return this;
    },

    nextYear() {
        this.year++;
        return this;
    },

    nextMonth() {
        this.month++;
        return this;
    },

    nextDay() {
        this.day++;
        return this;
    },

    startOfYear() {
        this.month = 0;
        this.day = 1;
        return this.startOfDay();
    },

    startOfMonth() {
        this.day = 1;
        return this.startOfDay();
    },

    startOfWeek() {
        this.day -= this.getDayOfWeek();
        return this.startOfDay();
    },

    startOfDay() {
        this.hour = this.minute = this.second = this.millisecond = 0;
        return this;
    },

    endOfYear() {
        this.year++;
        this.month = 0;
        this.day = 0;
        return this.endOfDay();
    },

    endOfMonth() {
        this.month++;
        this.day = 0;
        return this.endOfDay();
    },

    endOfWeek() {
        this.day += 6 - this.getDayOfWeek();
        return this.endOfDay();
    },

    endOfDay() {
        this.hour = 23;
        this.minute = 59;
        this.second = 59;
        this.millisecond = 999;
        return this;
    },

    __date: true
};


/**
 * Date stored as UTC but parsed by default as local time, and with arithmetic and string formatting in local time.
 */
let $dateLocal = function(when) {
    if (typeof when === 'string') {
        const match = when.match(re_date);
        if (match) {
            let value;
            if (match[9] == null && testOffset == null) {
                this.value = new Date(
                    (match[1]),
                    (match[2] - 1 | 0),
                    (match[3] || 1),
                    (match[4] | 0),
                    (match[5] | 0),
                    (match[7] | 0),
                    (match[8] | 0)
                );
            } else {
                const utc = Date.UTC(
                    (match[1]),
                    (match[2] - 1 | 0),
                    (match[3] || 1),
                    (match[4] | 0) - (match[10] | 0),
                    (match[5] | 0) - (match[11] | 0),
                    (match[7] | 0),
                    (match[8] | 0)
                );
                if (testOffset != null) {
                    this.offset = getOffset(testOffset, utc - (match[9] ? 0 : baseOffset));
                    this.value = new Date(utc - (match[9] ? 0 : this.offset));
                } else {
                    this.value = new Date(utc);
                }
            }
        }
    } else {
        const value = +(this.value = new Date(when === void 0 ? now() : when));
        if (testOffset != null && !isNaN(value)) {
            this.offset = when?.offset != null ? when.offset : getOffset(testOffset, value - baseOffset);
        }
    }
};


$dateLocal.prototype = {
    clone() {
        this.syncOffset();
        return new $dateLocal(this);
    },

    toUTC() {
        return new $dateUTC(this);
    },

    isLocal() {
        return true;
    },

    getDayOfWeek() {
        if (this.offset != null) {
            const value = this.value;
            const time = value.getTime();
            value.setTime(value.getTime() + this.offset);
            const r = (this.value.getUTCDay() + 6) % 7;
            value.setTime(time);
            return r;
        } else {
            return (this.value.getDay() + 6) % 7;
        }
    },

    getOffset() {
        return this.offset != null ? this.offset : -this.value.getTimezoneOffset();
    },

    /**
     * If `$date.data.timeZone` is not the local time zone, offset adjustment needs to happen manually.
     */
    syncOffset() {
        if (testOffset) {
            const value = +this.value;
            if (!isNaN(value)) {
                const offset = getOffset(testOffset, value);
                if (this.offset != offset) {
                    this.value.setTime(value + this.offset - offset);
                    this.offset = offset;
                }
            }
        }
    },

    /**
     * Retrieve ISO 8601 representation of date and time, in local time zone.
     */
    toString() {
        const value = this.value;
        let off = this.offset != null ? this.offset / 6e4 : -this.value.getTimezoneOffset();
        const sign = off < 0 ? '-' : '+';
        off = Math.abs(off);
        const time = value.getTime();
        if (!isNaN(time)) {
            if (this.offset != null) {
                value.setTime(time + this.offset);
                const r = value.getUTCFullYear() + '-' +
                    pad(value.getUTCMonth()+1) + '-' +
                    pad(value.getUTCDate()) + 'T' +
                    pad(value.getUTCHours()) + ':' +
                    pad(value.getUTCMinutes()) + ':' +
                    pad(value.getUTCSeconds()) + sign +
                    pad(off / 60 | 0) +
                    pad(off % 60);
                value.setTime(time);
                return r;
            } else {
                return value.getFullYear() + '-' +
                    pad(value.getMonth()+1) + '-' +
                    pad(value.getDate()) + 'T' +
                    pad(value.getHours()) + ':' +
                    pad(value.getMinutes()) + ':' +
                    pad(value.getSeconds()) + sign +
                    pad(off / 60 | 0) +
                    pad(off % 60);
            }
        }
    },

    /**
     * Retrieve ISO 8601 representation of date only, in local time zone.
     */
    toDateString() {
        const value = this.value;
        const time = value.getTime();
        if (!isNaN(time)) {
            if (this.offset != null) {
                value.setTime(time + this.offset);
                const r = value.getUTCFullYear() + '-' +
                    pad(value.getUTCMonth()+1) + '-' +
                    pad(value.getUTCDate());
                value.setTime(time);
                return r;
            } else {
                return value.getFullYear() + '-' +
                    pad(value.getMonth()+1) + '-' +
                    pad(value.getDate());
            }
        }
    },

    /**
     * Called by JSON.stringify.
     */
    toJSON() {
        return this.toString();
    },

    ...prototype
};


/**
 * Date stored as UTC and parsed by default as UTC, with arithmetic and string formatting in UTC time.
 */
let $dateUTC = function(when) {
    if (typeof when === 'string') {
        const match = when.match(re_date);
        if (match) {
            this.value = new Date(Date.UTC(
                (match[1]),
                (match[2] - 1 | 0),
                (match[3] || 1),
                (match[4] | 0) - (match[10] | 0),
                (match[5] | 0) - (match[11] | 0),
                (match[7] | 0),
                (match[8] | 0) - (match[9] | 0)
            ));
        } else {
            this.value = new Date(when);
        }
    } else {
        this.value = new Date(when === void 0 ? now() : when);
    }
};


$dateUTC.prototype = {
    clone() {
        return new $dateUTC(this);
    },

    toLocal() {
        return new $dateLocal(this);
    },

    isLocal() {
        return false;
    },

    getDayOfWeek() {
        return (this.value.getUTCDay() + 6) % 7;
    },

    getOffset() {
        return 0;
    },

    /**
     * Retrieve ISO 8601 representation of date and time, adjusted to UTC.
     */
    toString() {
        return isNaN(+this.value)
            ? void 0
            : this.value.toISOString();
    },

    /**
     * Retrieve ISO 8601 representation of date only, adjusted to UTC.
     */
    toDateString() {
        const value = this.value;
        return isNaN(+value)
            ? void 0
            : value.getUTCFullYear() + '-' +
                pad(value.getUTCMonth()+1) + '-' +
                pad(value.getUTCDate());
    },

    /**
     * Called by JSON.stringify.
     */
    toJSON() {
        return this.toString();
    },

    ...prototype
};


const $dateFormat = function(date) {
    if (typeof date === 'string') {
        date = new $dateLocal(date);
    }
    return !date || isNaN(+date) ? emDash : this.format(+date);
}


const $dateFormatISO = function(date) {
    return date.__date === true
        ? date.toString()
        : new $dateLocal(date).toString();
}


const $dateFormatISODate = function(date) {
    return date.__date === true
        ? date.toDateString()
        : new $dateLocal(date).toDateString();
}


/**
 * Conditional date formatting depending when the date occurred.
 */
const conditional = function(date) {
    if (typeof date === 'string') {
        date = new $dateLocal(date);
    }
    if (!date || isNaN(+date)) {
        return emDash;
    }

    const base = now();
    const diff = base - date + $date.offset;
    return (
        this.future       && diff < 0                       ? this.future :
        this.justNow      && diff < unitSecond * 45         ? this.justNow :
        this.pastMinute   && diff < unitMinute              ? this.pastMinute :
        this.pastHour     && diff < unitHour                ? this.pastHour :
        this.today        && diff < unitHour * 25 && diff >= 0 &&
            +$date.local(date).startOfDay() ===
            +$date.local(base).startOfDay()                 ? this.today :
        this.yesterday    && diff < unitHour * 49 && diff >= 0 &&
            +$date.local(date).startOfDay() ===
            +$date.local(base).previousDay().startOfDay()   ? this.yesterday :
        this.pastDay      && diff < unitDay                 ? this.pastDay :
        this.thisYear     && diff < unitDay * 366 && diff >= 0 &&
            +$date.local(date).startOfYear() ===
            +$date.local(base).startOfYear()                ? this.thisYear :
        this.lastYear     && diff < unitDay * 731 && diff >= 0 &&
            +$date.local(date).startOfYear() ===
            +$date.local(base).previousYear().startOfYear() ? this.lastYear :
        this.pastYear     && diff < unitYear                ? this.pastYear :
        this.other)(date);
}


const $date = Vue.observable(/** @alias $date */ {
    /** Configuration. */
    data: {
        /** @member {string} - Locale code used for date formatting. This could potentially differ from [`$locale.current`](#/global/locale). */
        locale: void 0,
        /** @member {string} - IANA time zone name used for local date formatting and arithmetic. Potentially `undefined` on Safari 9 or earlier, or JavaFX. */
        timeZone: void 0,
        /** @member {number} - Day number for start of calendar week, where `0` means Monday. */
        weekStart: void 0,
        /** @member {boolean} - Whether to use 12-hour time (as opposed to 24-hour time). */
        hour12: void 0,
        /** @member {array} - Period names, for example `['AM','PM']` in American English. */
        period: []
    },

    /** @member {number} - Estimated time difference between server and user agent in milliseconds (including half of a round trip); used in `$date.ago` and `$date.conditional`. */
    offset: 0,
    /** @ignore */
    now,
    /** @ignore */
    local: void 0,
    /** @ignore */
    utc: void 0,
    /** @ignore */
    formatter: void 0,
    ...formatters,

    /** @private */
    _use(locale) {
        const dopt = {
            numberingSystem: 'latn',
            calendar: 'gregory',
            timeZone: 'UTC',
            hour: 'numeric'
        };
        const fmt24 = new Intl.DateTimeFormat(locale, dopt);
        const fmt12 = new Intl.DateTimeFormat(locale, { hour12: true, ...dopt });

        /** @ignore */
        $date.data.locale = locale;
        /** @ignore */
        $date.data.weekStart =
            $locale.region ?
                (regionSun.includes($locale.region) ? 6 :
                 regionSat.includes($locale.region) ? 5 : 0) :
                (languageSun.includes($locale.language) ? 6 :
                 languageSat.includes($locale.language) ? 5 : 0);
        /** @ignore */
        $date.data.hour12 =
            parseInt(fmt24.format(468e5)) == 1;
        /** @ignore */
        $date.data.period = [
            fmt12.format(0).slice(3),
            fmt12.format(432e5).slice(3)
        ];

        const formatter = options => $dateFormat.bind(new Intl.DateTimeFormat(locale, {
            numberingSystem: 'latn',
            calendar: 'gregory',
            timeZone: testOffset ? $date.data.timeZone : void 0,
            ...options
        }));

        /**
         * Construct a `$date` object that will use local time for both arithmetic and formatting (for example, the day
         * starts at midnight local time). If specified as a date string without a time zone, the local time zone will be
         * assumed. Front end applications use `$date.local` more frequently than `$date.utc`.
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        $date.local = when => new $dateLocal(when);

        /**
         * Construct a `$date` object that will use UTC time for both arithmetic and formatting (for example, the day
         * starts at midnight UTC). If specified as a date string without a time zone, UTC time will be assumed.
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        $date.utc = when => new $dateUTC(when);

        /**
         * @function
         * @description Generate a formatting function with the specified options. When possible, use one of the
         * pregenerated methods such as `$date.mmmdy` or `$date.hms` for performance and compactness.
         * @param {Object} options - Options, as supported by `Intl.DateTimeFormat`.
         * @param {string} options.numberingSystem - Numbering system. This defaults to `latn` regardless of locale.
         * @param {string} options.dayPeriod - The way day periods (AM or PM) should be expressed.
         * @param {boolean} options.hour12 - Whether to use 12-hour time (as opposed to 24-hour time).
         * @param {string} options.hourCycle - The hour cycle to use. Possible values are `h11`, `h12`, `h23`, or `h24`.
         * @param {string} formatMatcher - The format matching algorithm to use. Possible values are `basic` and `best fit`; the default is `best fit`.
         * @param {string} weekday - The representation of the weekday. Possible values are `narrow`, `short`, `long`.
         * @param {string} year - The representation of the year. Possible values are `numeric`, `2-digit`.
         * @param {string} month - The representation of the month. Possible values are `numeric`, `2-digit`, `narrow`, `short`, `long`.
         * @param {string} day - The representation of the day. Possible values are `numeric`, `2-digit`.
         * @param {string} hour - The representation of the hour. Possible values are `numeric`, `2-digit`.
         * @param {string} minute - The representation of the minute. Possible values are `numeric`, `2-digit`.
         * @param {string} second - The representation of the second. Possible values are `numeric`, `2-digit`.
         * @param {string} timeZoneName - The representation of the time zone name. Possible values are `short`, `long`.
         */
        $date.formatter = formatter;

        /**
         * @function
         * @description Generate a formatting function that calls another formatting function, conditional on when the date occurred with respect to the current date.
         *
         * $date.conditional sample usage:
         *     computed: {
         *         myDateFormat: $date.conditional({
         *             today: $date.hm,       // 11:56 PM or 23:56
         *             other: $date.short     // Jun 26, 2019 11:56 PM or 26 Jun 2019, 23:56
         *         })
         *     }
         *
         * @param {Object} options
         * @param {function} options.future - Formatter for dates in the future.
         * @param {function} options.justNow - Formatter for dates less than 45 seconds ago.
         * @param {function} options.pastMinute - Formatter for dates within the past minute.
         * @param {function} options.pastHour - Formatter for dates within the past hour.
         * @param {function} options.today - Formatter for dates that occurred today, local time.
         * @param {function} options.yesterday - Formatter for dates that occurred yesterday, local time.
         * @param {function} options.pastDay - Formatter for dates within the past day.
         * @param {function} options.thisYear - Formatter for dates that occurred this year, local time.
         * @param {function} options.lastYear - Formatter for dates that occurred last year, local time.
         * @param {function} options.pastYear - Formatter for dates within the past year.
         * @param {function} options.other - Formatter for dates not matched by any of the other options.
         */
        $date.conditional = options => conditional.bind(options);

        /**
         * @function
         * @description Format as ISO 8601 date and time (2017-06-28T13:06:24-0400).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        $date.iso = $dateFormatISO;
        /**
         * @function
         * @description Format as ISO 8601 date (2017-06-28).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        $date.isodate = $dateFormatISODate;

        /**
         * @function
         * @name hm
         * @memberof $date
         * @description Format as hours and minutes in numeric format (1:06 PM or 13:06).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        /**
         * @function
         * @name hms
         * @memberof $date
         * @description Format as hours, minutes, and seconds in numeric format (1:06:24 PM or 13:06:24).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        /**
         * @function
         * @name hmsz
         * @memberof $date
         * @description Format as hours, minutes, and seconds in numeric format plus tz (1:06:24 PM EDT or 13:06:24 EDT).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        /**
         * @function
         * @name hhmm
         * @memberof $date
         * @description Format as hours and minutes in 2-digit format (01:06 PM or 13:06).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        /**
         * @function
         * @name hhmmss
         * @memberof $date
         * @description Format as hours, minutes, and seconds in 2-digit format (01:06:24 PM or 13:06:24).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        /**
         * @function
         * @name hhmmssz
         * @memberof $date
         * @description Format as hours, minutes, and seconds in 2-digit format plus tz (01:06:24 PM EDT or 13:06:24 EDT).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        /**
         * @function
         * @name m
         * @memberof $date
         * @description Format as month in numeric format (6).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        /**
         * @function
         * @name d
         * @memberof $date
         * @description Format as day in numeric format (28).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        /**
         * @function
         * @name y
         * @memberof $date
         * @description Format as year in numeric format (2017).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        /**
         * @function
         * @name mdyhm
         * @memberof $date
         * @description Format as month, day, year, hour, minute in numeric format (6/28/2017, 1:06 PM).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        /**
         * @function
         * @name mdyhms
         * @memberof $date
         * @description Format as month, day, year, hour, minute, second in numeric format (6/28/2017, 1:06:24 PM).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        /**
         * @function
         * @name mdyhmsz
         * @memberof $date
         * @description Format as month, day, year, hour, minute, second in numeric format plus tz (6/28/2017, 1:06:24 PM EDT).
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        Object.keys(formatters).forEach(name => {
            const option = formatters[name];
            $date[name] = formatter({
                weekday: option[0],
                day: option[1],
                month: option[2],
                year: option[3],
                hour: option[4],
                minute: option[5],
                second: option[6],
                timeZoneName: option[7]
            });
        });

        const ld = $locale.data;

        /**
         * @function
         * @description Format as "Just now", "10 min", "3 hrs", etc. Dates not within the past day but within the past
         * year will be formatted as "Jun 28" or "28 Jun". Dates not within the past year will be formatted as "Jun 28,
         * 2020" or "28 Jun 2020". If you use this function with dates received from the server, please set
         * `$date.offset` to help ensure accuracy.
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        $date.ago = conditional.bind({
            justNow: () => ld.dateNow,
            pastHour: date => {
                let t = Math.round((now() - date + $date.offset) / unitMinute);
                return ld.dateMins.replace('{t}', t);
            },
            pastDay: date => {
                let t = Math.round((now() - date + $date.offset) / unitHour);
                return (t == 1 ? ld.dateHr : ld.dateHrs).replace('{t}', t);
            },
            pastYear: $date.mmmd,
            other: $date.mmmdy
        });

        /**
         * @function
         * @description Format as "Just now", "10 minutes", "3 hours", etc. Dates not within the past day but within
         * the past year will be formatted as "June 28" or "28 June". Dates not within the past year will be formatted
         * as "June 28, 2020" or "28 June 2020". If you use this function with dates received from the server, please
         * set `$date.offset` to help ensure accuracy.
         * @param {string|number|$date} value - ISO date string, milliseconds timestamp, or `$date` object.
         */
        $date.agoLong = conditional.bind({
            justNow: () => ld.dateNow,
            pastHour: date => {
                let t = Math.round((now() - date + $date.offset) / unitMinute);
                return (t == 1 ? ld.dateMinute : ld.dateMinutes).replace('{t}', t);
            },
            pastDay: date => {
                let t = Math.round((now() - date + $date.offset) / unitHour);
                return (t == 1 ? ld.dateHour : ld.dateHours).replace('{t}', t);
            },
            pastYear: $date.mmmmd,
            other: $date.mmmmdy
        });

        /* trigger re-bind of v-date elements */
        dirty++;
    }
});


/**
 * @private
 * Set the time zone name used for local date formatting and arithmetic.
 */
function setTimeZone(timeZone) {
    /** @ignore */
    $date.data.timeZone = void 0;
    baseOffset = void 0;
    testOffset = void 0;

    const localTimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone && timeZone !== localTimeZone) {
        try {
            const formatter = new Intl.DateTimeFormat('en-US', {
                year: numeric,
                month: numeric,
                day: numeric,
                hour: numeric,
                minute: numeric,
                second: numeric,
                timeZone
            });
            const offset = getOffset(formatter, Date.now());
            if (!isNaN(offset)) {
                /** @ignore */
                $date.data.timeZone = timeZone;
                baseOffset = offset;
                testOffset = formatter;
            }
        } catch (e) {
            console.error(e);
        }
    }

    if (!testOffset) {
        /** @ignore */
        $date.data.timeZone = localTimeZone;
    }
    if ($date.data.locale) {
        $date._use($date.data.locale);
    }

    // $date.local accessors
    Object.keys(accessors).forEach(accessor => {
        Object.defineProperty($dateLocal.prototype, accessor, {
            get: new Function(testOffset
                ? 'var a=this.value.getTime();' +
                  'this.value.setTime(a+this.offset);' +
                  'var r=this.value.getUTC' + accessors[accessor] + '();' +
                  'this.value.setTime(a);' +
                  'return r'
                : 'return this.value.get' + accessors[accessor] + '()'),
            set: new Function('a', testOffset
                ? 'this.value.setTime(this.value.getTime()+this.offset);' +
                  'this.value.setUTC' + accessors[accessor] + '(a);' +
                  'this.value.setTime(this.value.getTime()-this.offset)'
                : 'this.value.set' + accessors[accessor] + '(a)'),
            configurable: true
        });
    });
}

// $date.utc accessors
Object.keys(accessors).forEach(accessor => {
    Object.defineProperty($dateUTC.prototype, accessor, {
        get: new Function('return this.value.getUTC' + accessors[accessor] + '()'),
        set: new Function('a', 'this.value.setUTC' + accessors[accessor] + '(a)')
    });
});


export {
    dirty,
    setTimeZone
};
export default $date;
